import React, {
  useCallback, useEffect, useMemo, useReducer,
} from 'react';
import withErrorBoundary from '@/utils/errorBoundary';
import * as S from './GatorResolveHandler.styles';
import { useHistory } from 'react-router';
import { FormContent } from './components';
import useLocale from '@/hooks/useLocale/useLocale';
import { useResize } from '@/hooks/useResize';
import { useDispatch, useSelector } from 'react-redux';
import { config } from '@/config';
import { generateRecaptchaToken, loadReCaptcha } from '@/utils/Application/reCaptcha';
import logger from '@/utils/logger';
import { preChatActions, sitesActions } from '@/redux/modules';

const initialState = {
  message: '',
  ticketId: '',
  selectedProduct: {
    label: '',
    name: '',
    value: undefined,
  },
  selectedService: {
    label: '',
    name: '',
    value: undefined,
  },
  errors: {
    message: {
      value: false,
    },
    ticketId: {
      value: false,
    },
    selectedProduct: {
      value: false,
    },
    selectedService: {
      value: false,
    },
  },
};

export function formReducer(state, action) {
  switch (action.type) {
    case 'SET_MESSAGE':
      return { ...state, message: action.payload };
    case 'SET_TICKET_ID':
      return { ...state, ticketId: action.payload };
    case 'SET_SELECTED_PRODUCT':
      return { ...state, selectedProduct: action.payload };
    case 'SET_SELECTED_SERVICE':
      return { ...state, selectedService: action.payload };
    case 'SET_ERRORS':
      return { ...state, errors: action.payload };
    default:
      return state;
  }
}

const GatorResolveHandler = () => {
  const { width } = useResize();
  const history = useHistory();
  const dispatch = useDispatch();
  const { ticket: { forms: { gatorResolve } = {} } = {} } = useLocale();
  const { gatorResolve: gatorResolveForms } = useSelector(state => state.sites);
  const { routes: routesLocale } = useLocale();
  const { hostings = [], domains = [] } = useSelector(
    state => state.preChat.productsList || {},
  );

  const [state, dispatchForm] = useReducer(formReducer, initialState);

  const serviceOptions = [
    {
      id: '1',
      label: gatorResolve.form.service.options.chat,
      value: 1,
    },
    {
      id: '3',
      label: gatorResolve.form.service.options.ticket,
      value: 2,
    },
  ];
  const isMobile = width <= 960;
  const shouldRenderTicketService = state.selectedService.value === serviceOptions[1].value;
  const isLoading = gatorResolveForms.loading && !gatorResolveForms.completed;
  const hasFormBeenCompleted = !gatorResolveForms.loading && gatorResolveForms.completed;

  const enabledButton = useMemo(() => {
    const isValid = state.message.trim().length > 0
      && !!state.selectedProduct.value
      && !!state.selectedService.value
      && (hasFormBeenCompleted ? state.ticketId.trim().length > 0 : true);

    return isValid;
  }, [state.message, state.selectedProduct, state.selectedService, hasFormBeenCompleted, state.ticketId]);

  const hostingProducts = useMemo(() => hostings
    .filter(hosting => ['Active', 'Suspend'].includes(hosting.status))
    .map(product => ({
      value: product.id,
      label: product.domain ? `${product.name} (${product.domain})` : product.name,
      name: product.name,
    })), [hostings]);

  const domainsProducts = useMemo(() => domains
    .filter(domain => ['Active', 'Suspend'].includes(domain.status))
    .map(product => ({
      value: product.id,
      label: product.name,
      name: product.type,
    })), [domains]);


  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();

      try {
        const token = await generateRecaptchaToken(
          config.RECAPTCHA_SITE_KEY_FOXTROT,
          'gatorResolve',
        );

        const newErrors = {
          message: {
            ...state.errors.message,
            value: state.message.trim().length === 0,
          },
          selectedProduct: {
            ...state.errors.selectedProduct,
            value: state.selectedProduct.label.trim().length === 0,
          },
          selectedService: {
            ...state.errors.selectedService,
            value: state.selectedService.label.trim().length === 0,
          },
          ticketId: {
            ...state.errors.ticketId,
            value:
            state.selectedService.value === 2
            && state.ticketId.trim().length === 0,
          },
        };

        const hasErrors = Object.values(newErrors).some(error => error.value === true);

        if (hasErrors) {
          dispatchForm({ type: 'SET_ERRORS', payload: newErrors });
          return;
        }

        const data = {
          domain: state.selectedProduct.label,
          referer: state.selectedService.label,
          ticket: state.ticketId,
          message: state.message,
          recaptcha: token,
        };

        dispatch(sitesActions.gatorResolve.request(data));
      } catch (e) {
        logger.error('Gator resolve error', e);
      }
    },
    [state.message, state.selectedProduct, state.selectedService, state.ticketId, state.errors, dispatch],
  );

  const handleRedirectToSupportPage = useCallback(() => {
    history.push(`${routesLocale.help}`);
  }, [history, routesLocale]);

  const getUserProducts = useCallback(() => {
    dispatch(preChatActions.productByStatus.request({
      withCancelled: true,
      withPending: false,
    }));
  }, [dispatch]);


  useEffect(() => {
    getUserProducts();
  }, [getUserProducts]);

  useEffect(() => {
    loadReCaptcha();
  }, []);

  useEffect(() => () => {
    dispatch(sitesActions.gatorResolve.reset());
  }, [dispatch]);

  const productsListToSelect = useMemo(() => [...hostingProducts, ...domainsProducts], [hostingProducts, domainsProducts]);

  return (
    <S.Container data-testid="gator-resolve-handler">
      <FormContent
        testId="gator-resolve-form"
        isLoading={isLoading}
        state={state}
        shouldRenderTicketService={shouldRenderTicketService}
        handleRedirectToSupportPage={handleRedirectToSupportPage}
        title={isMobile ? gatorResolve.mobileTitle : gatorResolve.title}
        handleSubmit={handleSubmit}
        enabledButton={enabledButton}
        productsListToSelect={productsListToSelect}
        serviceOptions={serviceOptions}
        dispatchForm={dispatchForm}
        hasFormBeenCompleted={hasFormBeenCompleted}
      />
    </S.Container>
  );
};

export default withErrorBoundary(GatorResolveHandler, 'GatorResolveHandler');
